import { memo } from 'react';
import {
  Banknote,
  Home,
  TrendingUp,
  BarChart3,
  FileText,
  Heart,
  Car,
  Bitcoin,
  Shield,
  PiggyBank,
  Building2,
  Package,
  Scale,
  Receipt,
  CreditCard,
  Zap,
  Wallet,
  Briefcase,
  type LucideIcon
} from 'lucide-react';
import { AssetType } from '../types';

interface InventoryIconProps {
  assetType: AssetType;
  className?: string;
}

// Icon mapping for asset and liability types
const iconMap: Record<AssetType, LucideIcon> = {
  // Asset types
  bank_account: Banknote,
  real_estate: Home,
  shares: TrendingUp,
  /*
    **The two listed holdings, told apart** (VALE-997). Both carried the ASX
    mark, which is true of each and therefore says nothing about *which* —
    `srn-shares` and `hin-shares` are the same instrument held two ways, and the
    one thing a reader scanning the inventory needs from the icon is which way.
    `srn_shares` takes the managed-funds mark and `hin_shares` returns to the one
    it had before VALE-863.
  */
  srn_shares: TrendingUp,
  hin_shares: BarChart3,
  nursing_home_bonds: Heart,
  nursing_home_bond: Heart,
  motor_vehicles: Car,
  vehicle: Car,
  cryptocurrency: Bitcoin,
  life_insurance: Shield,
  superannuation: PiggyBank,
  public_entities: Building2,
  managed_funds: TrendingUp,
  private_company: Briefcase,
  other: Package,
  // Liability types
  funeral_burial: Heart,
  legal_professional: Scale,
  mortgage: Home,
  vehicle_loan: Car,
  tax_liability: Receipt,
  credit_card: CreditCard,
  personal_loan: Banknote,
  utility_bill: Zap,
  estate_expense: Wallet,
  other_liability: FileText
};

/**
 * Pure function component for displaying inventory icons
 * Maps asset types to their corresponding Lucide icons
 */
/*
  **The ASX mark is gone, and `/asx-logo.png` with it** (VALE-997).

  VALE-863 gave both listed types the exchange's own logo, on the argument that
  it is what a reader recognises a listed holding by. True, and it is why the
  two became indistinguishable: `srn-shares` and `hin-shares` are the *same*
  instrument, so a mark that says "ASX-listed" is equally true of both and
  cannot tell them apart in a list where telling them apart is the only thing
  the icon is there for. Two icons from the same set do that, and drop a raster
  asset and a `next/image` render from a 20px slot at the same time.
*/
export const InventoryIcon = memo(
  ({ assetType, className = 'h-5 w-5' }: InventoryIconProps) => {
    const Icon = iconMap[assetType] || Package;

    return <Icon className={className} />;
  }
);

InventoryIcon.displayName = 'InventoryIcon';
